// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useCallback, useRef, useState } from 'react';

import { devicesAPI, InfraImportRow } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

const VALID_STATUSES = ['active', 'reserve', 'retired', 'maintenance', 'deactive'];

// Common device fields + beacon-specific (deck, side, frame_number, geofence, geofence_area, lat, lng)
// and checkin-specific (floor, geofence, geofence_area, lat, lng)
// lat/lng are used as-is for checkin_station; mapped to geofence_lat/geofence_lng for beacon
// Radio config (channel, mac_address, output_power, datarate) applies to both beacon and checkin.
// geofence is the geofence mode 0-5.
const TEMPLATE_HEADER =
  'deveui,name,description,device_category,device_status,nwk_key,checkpoint,supplier,deck,side,frame_number,floor,geofence,geofence_area,lat,lng,channel,mac_address,output_power,datarate';

const BEACON_EXAMPLE =
  '<deveui>,<serial_nr>,<description>,lf_beacon,<active/reserve>,<nwk_key>,0,<lora.server.lifefinder.eu>,<deck>,<side>,<frame_nr>,<floor>,<geofence_mode 0-5>,<geofence_area>,<lat>,<lng>,<channel>,<mac_address>,<output_power>,<datarate>';

const CHECKIN_EXAMPLE =
  '<deveui>,<serial_nr>,<description>,lf_checkin,<active/reserve>,<nwk_key>,1,<lora.server.lifefinder.eu>,<deck>,<side>,<frame_nr>,<floor>,<geofence_mode 0-5>,<geofence_area>,<lat>,<lng>,<channel>,<mac_address>,<output_power>,<datarate>';

const CELL = {
  padding: '0.4vmin 0.8vmin',
  textAlign: 'left' as const,
  borderBottom: '1px solid #eee',
};
const HEAD = { ...CELL, background: '#f5f5f5', fontWeight: 600, borderBottom: '2px solid #ddd' };

interface ParsedRow extends InfraImportRow {
  _rowNum: number;
  _errors: string[];
}

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}

function parseCsvText(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = splitCsvLine(lines[0]).map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/^["']|["']$/g, '')
  );

  return lines.slice(1).map((line, i) => {
    const vals = splitCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (vals[idx] ?? '').trim();
    });

    const errs: string[] = [];

    const deveui = (obj.deveui || '').toLowerCase().replace(/[^0-9a-f]/g, '');
    if (!deveui || deveui.length !== 16) errs.push('deveui must be 16 hex characters');

    const name = (obj.name || '').trim();
    if (!name) errs.push('name is required');
    else if (name.length > 45) errs.push('name exceeds 45 characters');

    const category = obj.device_category;
    if (category !== 'lf_beacon' && category !== 'lf_checkin')
      errs.push('device_category must be lf_beacon or lf_checkin');

    if (category === 'lf_beacon') {
      if (!obj.deck || isNaN(parseInt(obj.deck, 10)))
        errs.push('deck required for lf_beacon (integer)');
      if (!obj.side || !obj.side.trim()) errs.push('side required for lf_beacon');
    }

    if (obj.device_status && !VALID_STATUSES.includes(obj.device_status))
      errs.push(`device_status must be one of: ${VALID_STATUSES.join(', ')}`);

    const rawLat = obj.geofence_lat ?? obj.lat;
    const rawLng = obj.geofence_lng ?? obj.lng;
    if (rawLat && isNaN(parseFloat(rawLat))) errs.push('lat must be a number');
    if (rawLng && isNaN(parseFloat(rawLng))) errs.push('lng must be a number');

    // Radio config (optional). Accept the common "mac_adress" misspelling as an alias.
    const channel = obj.channel ?? '';
    const macAddress = obj.mac_address ?? obj.mac_adress ?? '';
    const outputPower = obj.output_power ?? '';
    const datarate = obj.datarate ?? '';
    if (channel && isNaN(parseInt(channel, 10))) errs.push('channel must be an integer');
    if (outputPower && isNaN(parseInt(outputPower, 10)))
      errs.push('output_power must be an integer');
    if (datarate && isNaN(parseInt(datarate, 10))) errs.push('datarate must be an integer');
    if (obj.geofence && isNaN(parseInt(obj.geofence, 10)))
      errs.push('geofence (mode) must be an integer 0-5');

    return {
      _rowNum: i + 1,
      _errors: errs,
      deveui: deveui || obj.deveui,
      name,
      description: obj.description || '',
      device_category: (category === 'lf_beacon' ? 'lf_beacon' : 'lf_checkin') as
        | 'lf_beacon'
        | 'lf_checkin',
      device_status: obj.device_status || 'active',
      nwk_key: obj.nwk_key || '',
      checkpoint: obj.checkpoint || '0',
      supplier: obj.supplier || '',
      deck: obj.deck || '',
      side: obj.side || '',
      frame_number: obj.frame_number || '',
      floor: obj.floor || '',
      geofence: obj.geofence || '0',
      geofence_area: obj.geofence_area || '',
      lat: rawLat || '',
      lng: rawLng || '',
      channel,
      mac_address: macAddress,
      output_power: outputPower,
      datarate,
    };
  });
}

interface ImportResult {
  inserted: number;
  chirpstackSynced: boolean;
  skipped: { deveui: string; reason: string }[];
  errors: { deveui: string; reason: string }[];
}

export const InfrastructureCsvImport: React.FC = () => {
  const [rawText, setRawText] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const parse = useCallback((text: string) => {
    setRawText(text);
    setRows(parseCsvText(text));
    setResult(null);
    setSubmitError(null);
  }, []);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => parse((ev.target?.result as string) || '');
      reader.readAsText(file);
      e.target.value = '';
    },
    [parse]
  );

  const validRows = rows.filter((r) => r._errors.length === 0);
  const hasErrors = rows.some((r) => r._errors.length > 0);
  const beaconCount = rows.filter((r) => r.device_category === 'lf_beacon').length;
  const checkinCount = rows.filter((r) => r.device_category === 'lf_checkin').length;

  const onSubmit = useCallback(async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: InfraImportRow[] = validRows.map(
        ({ _rowNum: _r, _errors: _e, ...rest }) => rest
      );
      const res = await devicesAPI.importInfrastructure(payload);
      if (!res.success) throw new Error(res.error || 'Import failed');
      setResult({
        inserted: res.inserted,
        chirpstackSynced: res.chirpstackSynced,
        skipped: res.skipped,
        errors: res.errors,
      });
      setRows([]);
      setRawText('');
    } catch (err) {
      logger.error('infrastructure import failed', err);
      setSubmitError((err as Error).message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }, [validRows]);

  const downloadTemplate = () => {
    const blob = new Blob([`${TEMPLATE_HEADER}\n${BEACON_EXAMPLE}\n${CHECKIN_EXAMPLE}\n`], {
      type: 'text/csv',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'infrastructure-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vmin', padding: '1vmin' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 'max(14px, 2vmin)' }}>Beacon / Check-in Import</h3>
        <button
          onClick={downloadTemplate}
          style={{
            background: 'transparent',
            border: '1px solid #aaa',
            borderRadius: '0.4vmin',
            padding: '0.4vmin 1vmin',
            cursor: 'pointer',
            fontSize: 'max(11px, 1.5vmin)',
          }}
        >
          Download template
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1vmin', flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.6vmin',
            padding: '0.5vmin 1.2vmin',
            background: '#1976d2',
            color: 'white',
            borderRadius: '0.4vmin',
            cursor: 'pointer',
            fontSize: 'max(12px, 1.6vmin)',
          }}
        >
          Upload CSV
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
        </label>
        <span style={{ alignSelf: 'center', color: '#888', fontSize: 'max(11px, 1.5vmin)' }}>
          or paste below
        </span>
      </div>

      <textarea
        value={rawText}
        onChange={(e) => parse(e.target.value)}
        placeholder={`${TEMPLATE_HEADER}\n${BEACON_EXAMPLE}\n${CHECKIN_EXAMPLE}`}
        spellCheck={false}
        style={{
          width: '100%',
          minHeight: '10vmin',
          fontFamily: 'monospace',
          fontSize: 'max(11px, 1.4vmin)',
          resize: 'vertical',
          border: '1px solid #ccc',
          borderRadius: '0.4vmin',
          padding: '0.8vmin',
          boxSizing: 'border-box',
        }}
      />

      {rows.length > 0 && (
        <>
          <div style={{ fontSize: 'max(11px, 1.5vmin)', color: '#555' }}>
            {rows.length} row{rows.length !== 1 ? 's' : ''} parsed
            {beaconCount > 0 && (
              <span style={{ marginLeft: '1vmin', color: '#4A90E2' }}>
                {beaconCount} beacon{beaconCount !== 1 ? 's' : ''}
              </span>
            )}
            {checkinCount > 0 && (
              <span style={{ marginLeft: '0.6vmin', color: '#2b7a2b' }}>
                {checkinCount} check-in{checkinCount !== 1 ? 's' : ''}
              </span>
            )}
            {hasErrors && (
              <span style={{ color: '#c00', marginLeft: '1vmin' }}>
                ({rows.filter((r) => r._errors.length > 0).length} with errors — will be skipped)
              </span>
            )}
          </div>

          <div
            style={{
              overflow: 'auto',
              maxHeight: '40vh',
              border: '1px solid #ddd',
              borderRadius: '0.4vmin',
            }}
          >
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'max(11px, 1.4vmin)' }}
            >
              <thead>
                <tr>
                  {[
                    '#',
                    'deveui',
                    'name',
                    'category',
                    'status',
                    'deck',
                    'side',
                    'floor',
                    'geofence',
                    'geofence_area',
                    'lat',
                    'lng',
                    'channel',
                    'mac_address',
                    'output_power',
                    'datarate',
                    '',
                  ].map((h) => (
                    <th key={h} style={HEAD}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row._rowNum}
                    style={{ background: row._errors.length ? '#fff8f8' : undefined }}
                  >
                    <td style={CELL}>{row._rowNum}</td>
                    <td style={{ ...CELL, fontFamily: 'monospace' }}>{row.deveui}</td>
                    <td style={CELL}>{row.name}</td>
                    <td
                      style={{
                        ...CELL,
                        color: row.device_category === 'lf_beacon' ? '#4A90E2' : '#2b7a2b',
                        fontWeight: 600,
                      }}
                    >
                      {row.device_category}
                    </td>
                    <td style={CELL}>{row.device_status}</td>
                    <td style={CELL}>{String(row.deck || '')}</td>
                    <td style={CELL}>{String(row.side || '')}</td>
                    <td style={CELL}>{String(row.floor || '')}</td>
                    <td style={CELL}>{String(row.geofence || '0')}</td>
                    <td style={CELL}>{String(row.geofence_area || '')}</td>
                    <td style={CELL}>{String(row.lat || '')}</td>
                    <td style={CELL}>{String(row.lng || '')}</td>
                    <td style={CELL}>{String(row.channel || '')}</td>
                    <td style={{ ...CELL, fontFamily: 'monospace' }}>
                      {String(row.mac_address || '')}
                    </td>
                    <td style={CELL}>{String(row.output_power || '')}</td>
                    <td style={CELL}>{String(row.datarate || '')}</td>
                    <td style={{ ...CELL, color: '#c00', maxWidth: '20vmin' }}>
                      {row._errors.join('; ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '1vmin', alignItems: 'center' }}>
            <button
              onClick={onSubmit}
              disabled={submitting || validRows.length === 0}
              style={{
                padding: '0.7vmin 2vmin',
                background: validRows.length === 0 ? '#aaa' : '#7b2ff7',
                color: 'white',
                border: 'none',
                borderRadius: '0.4vmin',
                cursor: validRows.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: 'max(12px, 1.6vmin)',
                fontWeight: 600,
              }}
            >
              {submitting
                ? 'Importing…'
                : `Import ${validRows.length} device${validRows.length !== 1 ? 's' : ''}`}
            </button>
            <button
              onClick={() => {
                parse('');
                setRows([]);
              }}
              style={{
                padding: '0.7vmin 1.5vmin',
                background: 'transparent',
                border: '1px solid #aaa',
                borderRadius: '0.4vmin',
                cursor: 'pointer',
                fontSize: 'max(12px, 1.6vmin)',
              }}
            >
              Clear
            </button>
            {submitError && (
              <span style={{ color: '#c00', fontSize: 'max(11px, 1.5vmin)' }}>{submitError}</span>
            )}
          </div>
        </>
      )}

      {result && (
        <div
          style={{
            background: '#f0faf0',
            border: '1px solid #4caf50',
            borderRadius: '0.5vmin',
            padding: '1.2vmin',
            fontSize: 'max(12px, 1.6vmin)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '0.5vmin' }}>
            Import complete — {result.inserted} inserted
            {result.chirpstackSynced && (
              <span
                style={{
                  marginLeft: '1vmin',
                  fontWeight: 400,
                  color: '#2b7a2b',
                  fontSize: 'max(11px, 1.4vmin)',
                }}
              >
                ✓ Chirpstack sync triggered
              </span>
            )}
          </div>
          {result.skipped.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', color: '#666' }}>
                {result.skipped.length} skipped (already exist)
              </summary>
              <ul
                style={{
                  margin: '0.3vmin 0 0 1.5vmin',
                  padding: 0,
                  fontSize: 'max(11px, 1.4vmin)',
                }}
              >
                {result.skipped.map((s) => (
                  <li key={s.deveui}>
                    {s.deveui} — {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.errors.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', color: '#c00' }}>
                {result.errors.length} failed
              </summary>
              <ul
                style={{
                  margin: '0.3vmin 0 0 1.5vmin',
                  padding: 0,
                  fontSize: 'max(11px, 1.4vmin)',
                }}
              >
                {result.errors.map((e) => (
                  <li key={e.deveui}>
                    {e.deveui} — {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
